import {
  APIComponentAttributes,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import { ArdoqError, isArdoqError } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { GridApi, IRowNode } from 'ag-grid-enterprise';
import { uniq } from 'lodash';
import { createComponentsOperations } from '../createComponents/createComponentsOperations';
import { mergeScopeDataIntoInventoryContext } from '../mergeScopeDataIntoInventoryContext';
import { toInventoryReferenceRepresentation } from '../toInventoryReferenceRepresentation';
import {
  ColumnDefinitionArdoqMetadata,
  ExtendedRowNode,
  InventoryContext,
  InventoryReferenceRepresentation,
  ReferenceColumnDefinitionArdoqMetadata,
  SavingStatus,
} from '../types';
import { toPendingReferenceChangeFromValues } from './editReferences';
import { getInvalidParentIds } from './validation';

export type PastedNameResolution = {
  /** Cells whose names no component carries. */
  unmatchedNameCount: number;
  /** Cells whose names more than one component carries. */
  ambiguousNameCount: number;
  /** Parent cells naming a component that cannot be the parent. */
  ineligibleParentCount: number;
  /** Set when the lookup failed, so no name could be judged at all. */
  lookupError?: ArdoqError;
  /** Scope data holding the components the names resolved to. */
  scopeData: EnhancedScopeData;
};

type NamedCellTarget =
  | { kind: 'reference'; column: ReferenceColumnDefinitionArdoqMetadata }
  | { kind: 'parent' };

/** The names pasted into one cell, and the cell they landed in. */
type NamedCell = {
  node: ExtendedRowNode;
  field: string;
  editedComponent: APIComponentAttributes;
  target: NamedCellTarget;
  names: string[];
};

type NamedComponent = Pick<APIComponentAttributes, '_id' | 'rootWorkspace'>;

const PARENT_FIELD = 'parent';

const toNamedCellTarget = (
  api: GridApi<APIComponentAttributes>,
  field: string
): NamedCellTarget | null => {
  if (field === PARENT_FIELD) {
    return { kind: 'parent' };
  }
  const columnContext = api.getColumnDef(field)?.context as
    | ColumnDefinitionArdoqMetadata
    | undefined;
  return columnContext?.inventoryColumnType === 'reference'
    ? { kind: 'reference', column: columnContext }
    : null;
};

const toNamedCells = (
  api: GridApi<APIComponentAttributes>,
  node: IRowNode<APIComponentAttributes> | null | undefined
): NamedCell[] => {
  const extendedNode = node as ExtendedRowNode | undefined;
  const editedComponent = extendedNode?.data;
  if (!extendedNode || !editedComponent) {
    return [];
  }
  return Object.entries(extendedNode._pendingComponentNames ?? {}).flatMap(
    ([field, names]) => {
      const target = toNamedCellTarget(api, field);
      return target
        ? [{ node: extendedNode, field, editedComponent, target, names }]
        : [];
    }
  );
};

const collectNamedCells = (
  api: GridApi<APIComponentAttributes>
): NamedCell[] => {
  const namedCells: NamedCell[] = [];
  api.forEachNode(node => namedCells.push(...toNamedCells(api, node)));

  // forEachNode skips the pinned rows, where a just-created component sits
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      namedCells.push(...toNamedCells(api, node));
    }
  }

  return namedCells;
};

export const clearPendingComponentNamesFromNodes = (
  api: GridApi<APIComponentAttributes>
): void => {
  api.forEachNode(node => {
    (node as ExtendedRowNode)._pendingComponentNames = undefined;
  });
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (node) {
      (node as ExtendedRowNode)._pendingComponentNames = undefined;
    }
  }
};

/**
 * The lookup is a round trip, and a paste otherwise looks like it did nothing
 * until it comes back.
 */
const setLookupInProgress = (
  api: GridApi<APIComponentAttributes>,
  namedCells: NamedCell[],
  isInProgress: boolean
) => {
  const rowNodes = uniq(namedCells.map(({ node }) => node));
  rowNodes.forEach(node => {
    node._savingStatus = isInProgress ? SavingStatus.PENDING : undefined;
  });
  api.redrawRows({ rowNodes });
};

const toComponentsByName = (
  scopeData: APIScopeData
): Map<string, NamedComponent[]> => {
  const componentsByName = new Map<string, NamedComponent[]>();
  scopeData.components.forEach(({ _id, name, rootWorkspace }) => {
    const key = name.trim().toLowerCase();
    componentsByName.set(key, [
      ...(componentsByName.get(key) ?? []),
      { _id, rootWorkspace },
    ]);
  });
  return componentsByName;
};

/**
 * A parent sits in the component's own workspace and cannot be the component
 * or one of its descendants, so only those candidates count.
 */
const toCandidateFilter = (
  { editedComponent, target }: NamedCell,
  scopeData: EnhancedScopeData
): ((component: NamedComponent) => boolean) => {
  if (target.kind === 'reference') {
    return () => true;
  }
  const invalidParentIds = new Set(
    getInvalidParentIds(scopeData, editedComponent)
  );
  return ({ _id, rootWorkspace }) =>
    rootWorkspace === editedComponent.rootWorkspace &&
    !invalidParentIds.has(_id);
};

type NameMatch =
  | { outcome: 'matched'; componentIds: ArdoqId[] }
  | { outcome: 'unmatched' }
  | { outcome: 'ineligible' }
  | { outcome: 'ambiguous' };

/**
 * A cell is pasted whole or not at all, so one name nobody carries, or one
 * several components carry, leaves the whole cell alone.
 */
const toNameMatch = (
  names: string[],
  componentsByName: Map<string, NamedComponent[]>,
  isCandidate: (component: NamedComponent) => boolean
): NameMatch => {
  const namedComponents = names.map(
    name => componentsByName.get(name.toLowerCase()) ?? []
  );
  const matches = namedComponents.map(components =>
    components.filter(isCandidate).map(({ _id }) => _id)
  );
  if (matches.some(componentIds => componentIds.length > 1)) {
    return { outcome: 'ambiguous' };
  }
  if (namedComponents.some(components => components.length === 0)) {
    return { outcome: 'unmatched' };
  }
  if (matches.some(componentIds => componentIds.length === 0)) {
    return { outcome: 'ineligible' };
  }
  return { outcome: 'matched', componentIds: uniq(matches.flat()) };
};

/**
 * A paste replaces the cell, so the change is the same diff the clipboard's
 * ids go through: whatever the row referenced and the names do not is on its
 * way out.
 */
const applyReferenceMatch = (
  { node, field, editedComponent }: NamedCell,
  { referenceTypeName, direction }: ReferenceColumnDefinitionArdoqMetadata,
  componentIds: ArdoqId[],
  api: GridApi<APIComponentAttributes>,
  scopeData: EnhancedScopeData
) => {
  const pendingChange = toPendingReferenceChangeFromValues({
    componentId: editedComponent._id,
    referenceTypeName,
    oldReferences:
      api.getCellValue<InventoryReferenceRepresentation[]>({
        rowNode: node,
        colKey: field,
      }) ?? [],
    newReferences: componentIds
      // a name can land in the cell of the very component carrying it, which
      // the id path refuses too
      .filter(componentId => componentId !== editedComponent._id)
      .map(referencedComponentId =>
        toInventoryReferenceRepresentation({
          scopeData,
          editedComponent,
          referencedComponentId,
          direction,
          referenceTypeName,
        })
      ),
  });
  if (pendingChange) {
    node._pendingReferenceChanges = [
      ...(node._pendingReferenceChanges ?? []),
      pendingChange,
    ];
  }
};

/**
 * Written straight to the row rather than through `setDataValue`: AG Grid
 * delivers `cellValueChanged` asynchronously, after the flush has run.
 */
const applyParentMatch = (
  { node, editedComponent }: NamedCell,
  parentId: ArdoqId
) => {
  if (editedComponent.parent === parentId) {
    return;
  }
  node._pendingChanges = [
    ...(node._pendingChanges ?? []),
    {
      field: PARENT_FIELD,
      oldValue: editedComponent.parent ?? null,
      newValue: parentId,
    },
  ];
  node.updateData({ ...editedComponent, parent: parentId });
};

/**
 * Turns the names a paste left on the rows into pending changes, so the flush
 * that follows saves them alongside the ones the clipboard identified by id.
 */
export const resolvePastedComponentNames = async (
  api: GridApi<APIComponentAttributes>,
  context: InventoryContext
): Promise<PastedNameResolution> => {
  const namedCells = collectNamedCells(api);
  const nothingResolved = {
    unmatchedNameCount: 0,
    ambiguousNameCount: 0,
    ineligibleParentCount: 0,
    scopeData: context.scopeData,
  };
  if (namedCells.length === 0) {
    return nothingResolved;
  }
  clearPendingComponentNamesFromNodes(api);
  setLookupInProgress(api, namedCells, true);

  const fetched = await context.commands.fetchScopeDataForComponentNames(
    uniq(namedCells.flatMap(({ names }) => names))
  );
  setLookupInProgress(api, namedCells, false);
  if (isArdoqError(fetched)) {
    // a search that never came back says nothing about the names it carried
    return { ...nothingResolved, lookupError: fetched };
  }
  mergeScopeDataIntoInventoryContext(
    api,
    dateRangeOperations.getMergedDateRangeFieldForScopeData(fetched)
  );
  const { scopeData } = api.getGridOption('context') as InventoryContext;
  const componentsByName = toComponentsByName(fetched);

  let unmatchedNameCount = 0;
  let ambiguousNameCount = 0;
  let ineligibleParentCount = 0;

  namedCells.forEach(namedCell => {
    const match = toNameMatch(
      namedCell.names,
      componentsByName,
      toCandidateFilter(namedCell, scopeData)
    );
    if (match.outcome === 'ambiguous') {
      ambiguousNameCount += 1;
      return;
    }
    if (match.outcome === 'unmatched') {
      unmatchedNameCount += 1;
      return;
    }
    if (match.outcome === 'ineligible') {
      ineligibleParentCount += 1;
      return;
    }

    const { target } = namedCell;
    if (target.kind === 'parent') {
      applyParentMatch(namedCell, match.componentIds[0]);
      return;
    }
    applyReferenceMatch(
      namedCell,
      target.column,
      match.componentIds,
      api,
      scopeData
    );
  });

  return {
    unmatchedNameCount,
    ambiguousNameCount,
    ineligibleParentCount,
    scopeData,
  };
};
